import { apiEndpoint } from '../config/api';
import { apiFetch } from './api';

// API helper for Franchise Administration (design §15), following the seasonSequenceApi.js
// pattern: one function per endpoint, every failure thrown with the SERVER's message so callers
// can surface it. This replaces the inline apiFetch-per-handler style in ComCodes and, with it,
// the silent `.catch((err) => {})` blocks that made write failures invisible.
//
// Endpoint names are the existing deployed contract and are deliberately NOT renamed
// (future-refactor-scope.md: prefer additive endpoints; renaming in place is a last resort).

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Request failed with status ${response.status}`);
  }
  return body;
}

// Active + inactive BDMs. The assignment UI filters to active for NEW selection but must still be
// able to display an existing assignment to a since-deactivated BDM.
export async function listBdms() {
  const response = await apiFetch(`${apiEndpoint}/bdm-list`);
  return parseResponse(response);
}

// The two facility-site endpoints report failure as **HTTP 200 with an error string in `message`**
// (`"Error in deleting facilitysite"`, `"Error in updating facility_site"`) when the statement
// affected zero rows. `res.ok` is therefore not sufficient -- the classic UI checked only that and
// so swallowed every site failure silently. Inspect the body too.
function assertSiteMutationSucceeded(body) {
  if (body && typeof body.message === 'string' && /^error\b/i.test(body.message.trim())) {
    throw new Error(body.message);
  }
  return body;
}

// Create (facility_site_id === 0) or update a site. Field names are the existing deployed
// contract and are deliberately not renamed.
export async function saveFacilitySite(payload) {
  const response = await apiFetch(`${apiEndpoint}/facilitysite-add-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return assertSiteMutationSucceeded(await parseResponse(response));
}

export async function deleteFacilitySite(facilitySiteId) {
  const response = await apiFetch(`${apiEndpoint}/facilitysite-remove/${facilitySiteId}`, {
    method: 'DELETE',
  });
  return assertSiteMutationSucceeded(await parseResponse(response));
}

// Franchise Details: active + notes.
//
// PATCH-style, like updateFacilityOwnership and unlike updateFacilityDetails. It targets the NEW
// additive `franchise-settings-update` endpoint, never the legacy `franchise-update` (which is a
// full overwrite of all five settings columns and would let one dialog null another's fields).
//
// There is NO writable franchise email: lksi_account_rep_email belongs to the BDM default, and
// dim_franchise.email is warehouse-sourced and read-only. Stage 2 adds bdm_id (Track B).
export async function updateFranchiseOwnership(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-ownership-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

export async function updateFranchiseSettings(payload) {
  const response = await apiFetch(`${apiEndpoint}/franchise-settings-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// Facility Details: active + email + notes.
//
// NOT PATCH-style -- deliberately different from updateFacilityOwnership below. The deployed
// `facility-update` handler does a FULL OVERWRITE of exactly lksi_is_active / lksi_email /
// lksi_notes, so every call must send all three or an omitted one is written as NULL. That is
// safe here precisely because those three columns ARE the whole "facility details" concern, and
// the handler does not touch bdm_id / pacesetter / next_level_leader (design §10).
//
// The field names are the existing deployed contract (locationLKSIEmail / locationLKSINotes /
// isLKSIActive), kept as-is rather than renamed.
export async function updateFacilityDetails(payload) {
  const response = await apiFetch(`${apiEndpoint}/facility-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// PATCH-style: `payload` carries ONLY the fields that actually changed, plus dim_facilityID.
// An omitted key is preserved server-side; an explicit null clears it (restoring inheritance for
// bdm_id, or "not configured" for the two flags). Sending only what changed is what keeps an
// unrelated edit from tripping the inactive-BDM 409 on a facility whose current BDM is inactive.
export async function updateFacilityOwnership(payload) {
  const response = await apiFetch(`${apiEndpoint}/facility-ownership-update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}
